import { Badge, Banner, BlockStack, Button, Icon, InlineStack, List, Modal, Text, TextField } from '@shopify/polaris';
import { PlayIcon, ProductCostIcon, UndoIcon } from '@shopify/polaris-icons';
import { StyledTemplateButtons, StyledVariableList } from './styled';
import { useEffect, useMemo, useState } from 'react';
import { ButtonType } from '../../config';
import { TrackingStatus, VariableTags } from '@/constants';
import { Receiver } from '@/constants/enum';
import {
  useGetNotificationAccountsQuery,
  useResendSesEmailMutation,
  useResetToDefaultMutation,
  useSendTestEmailMutation,
} from '@/redux/api/api.caller';
import { useDispatch, useSelector } from 'react-redux';
import { emailTemplatesTabSelector, orderStatusSelector } from '@/redux/features/email.slice';
import commonSlice from '@/redux/features/common.slice';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import { CopyIcon } from '@/icons';
import { ErrorBanner } from '@/components';

interface IModal {
  isActive: boolean;
  type: ButtonType;
}

// TR-564 P0-2: cooldown khớp backend rate-limit (90s) để tránh hit lỗi 429 ngay sau khi gửi.
const TEST_EMAIL_COOLDOWN = 90;
const isEmailValid = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const getStatusLabel = (status: TrackingStatus) =>
  (Object.keys(TrackingStatus) as (keyof typeof TrackingStatus)[]).find((key) => TrackingStatus[key] === status) || status;

const TemplateButtons = () => {
  const [apiError, setApiError] = useState<string>('');
  const [modal, setModal] = useState<IModal>({ isActive: false, type: ButtonType.InsertTags });
  const dispatch = useDispatch();
  const orderStatusState = useSelector(orderStatusSelector);
  const templatesTab = useSelector(emailTemplatesTabSelector);
  const { data: accountsData } = useGetNotificationAccountsQuery();
  const recipientEmail = accountsData?.data?.email?.account?.recipientEmail || '';
  const [sendTestEmail, sendTestEmailData] = useSendTestEmailMutation();
  const [resetToDefault, resetToDefaultData] = useResetToDefaultMutation();
  // TR-597: resend verify cho sender own khi backend báo chưa verify.
  const [resendSesEmail, resendSesEmailData] = useResendSesEmailMutation();

  // TR-564 P0-2: state cho modal Send test (input email + banner + cooldown).
  const [testEmail, setTestEmail] = useState<string>('');
  const [touched, setTouched] = useState<boolean>(false);
  const [testError, setTestError] = useState<string>('');
  const [sentTo, setSentTo] = useState<string>('');
  const [cooldown, setCooldown] = useState<number>(0);
  // TR-597: email sender own chưa verify (từ lỗi SENDER_NOT_VERIFIED) → bật nút "Verify sender".
  const [senderEmail, setSenderEmail] = useState<string>('');

  const isSendTestModal = modal.isActive && modal.type === ButtonType.SendTestEmail;

  // Prefill recipientEmail khi mở modal Send test. Dùng `prev || recipientEmail` để:
  // (a) fill khi accountsData về muộn, (b) KHÔNG ghi đè email merchant đã gõ. handleCloseModal
  // reset testEmail='' nên mỗi lần mở lại luôn prefill sạch.
  useEffect(() => {
    if (isSendTestModal) setTestEmail((prev) => prev || recipientEmail);
  }, [isSendTestModal, recipientEmail]);

  // Cooldown countdown sau khi gửi thành công.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const invalid = touched && !isEmailValid(testEmail);
  const canSendTest = isEmailValid(testEmail) && cooldown === 0 && !sendTestEmailData.isLoading;
  const receiverLabel = templatesTab === Receiver.Merchant ? 'To yourself' : 'To customer';

  const modalInfo = useMemo(() => {
    switch (modal.type) {
      case ButtonType.ResetToDefault:
        return {
          title: 'Reset to default',
          content: 'Are you sure you want to reset this email template to its default settings?',
          okBtn: 'Yes',
          cancelBtn: 'No',
        };
      default:
        return {
          title: 'Insert tags',
          content: 'Choose a tag to insert into the email content below:',
          cancelBtn: 'Cancel',
        };
    }
  }, [modal.type]);

  const handleCloseModal = () => {
    setApiError('');
    // TR-564: reset trạng thái modal Send test khi đóng (cooldown, email, banner) để lần mở sau sạch.
    setTestEmail('');
    setTouched(false);
    setTestError('');
    setSentTo('');
    setCooldown(0);
    setSenderEmail('');
    setModal((prev) => ({ ...prev, isActive: false, type: ButtonType.InsertTags }));
  };

  const handleClickBtn = (type: ButtonType) => setModal((prev) => ({ ...prev, isActive: true, type }));

  const handleResetToDefault = () => {
    resetToDefault({ trackingStatus: orderStatusState, receiver: templatesTab }).then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        if (apiError) setApiError('');
        dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
        handleCloseModal();
        return;
      }
      setApiError(getErrorFromAPI(res));
    });
  };

  const handleSendTestEmail = () => {
    if (!isEmailValid(testEmail)) {
      setTouched(true);
      return;
    }
    setTestError('');
    setSenderEmail('');
    sendTestEmail({ trackingStatus: orderStatusState, receiver: templatesTab, email: testEmail.trim() }).then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        setSentTo(testEmail.trim());
        setCooldown(TEST_EMAIL_COOLDOWN);
        return;
      }
      const errData = 'error' in res ? ((res.error as { data?: Record<string, unknown> })?.data ?? {}) : {};
      // TR-599: lỗi rate-limit → seed đếm ngược ở nút (không hiện banner "wait" tĩnh).
      const seconds = Number(String(errData?.message ?? '').match(/(\d+)\s*second/i)?.[1]) || 0;
      if (seconds > 0) {
        setCooldown(seconds);
        return;
      }
      // TR-597: sender own chưa verify → lưu senderEmail để nút "Verify sender" gửi verify.
      if (errData?.error === 'SENDER_NOT_VERIFIED') setSenderEmail(String(errData.senderEmail ?? ''));
      setTestError(getErrorFromAPI(res));
    });
  };

  // TR-597: bấm "Verify sender" → gửi verify mail cho sender own của shop.
  const handleResendVerify = () => {
    if (!senderEmail) return;
    resendSesEmail({ email: senderEmail }).then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        setTestError('');
        setSenderEmail('');
        dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
        return;
      }
      // Gửi verify lỗi → hiện message, giữ nút để user thử lại.
      setTestError(getErrorFromAPI(res));
    });
  };

  return (
    <StyledTemplateButtons>
      <div className="btn-group">
        <Button icon={<Icon source={ProductCostIcon} tone="base" />} onClick={() => handleClickBtn(ButtonType.InsertTags)}>
          Insert tags
        </Button>
        <Button icon={<Icon source={UndoIcon} tone="base" />} onClick={() => handleClickBtn(ButtonType.ResetToDefault)}>
          Reset to default
        </Button>
        <Button icon={<Icon source={PlayIcon} tone="base" />} onClick={() => handleClickBtn(ButtonType.SendTestEmail)}>
          Send test email
        </Button>
      </div>

      {/* Insert tags / Reset to default */}
      <Modal
        open={modal.isActive && modal.type !== ButtonType.SendTestEmail}
        onClose={handleCloseModal}
        title={modalInfo.title}
        primaryAction={
          modal.type === ButtonType.ResetToDefault
            ? {
                content: modalInfo.okBtn as string,
                onAction: handleResetToDefault,
                loading: resetToDefaultData.isLoading,
              }
            : undefined
        }
        secondaryActions={[{ content: modalInfo.cancelBtn, onAction: handleCloseModal }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <ErrorBanner isVisible={!!apiError}>{apiError}</ErrorBanner>
            <p>{modalInfo.content}</p>
            {modal.type === ButtonType.InsertTags && (
              <StyledVariableList>
                <List>
                  {Object.values(VariableTags).map((variable) => (
                    <List.Item key={variable}>
                      <div className="variable-item">
                        <div className="variable-badge">
                          <Badge tone="info">{`{{!! ${variable} !!}}`}</Badge>
                        </div>
                        <CopyIcon copiedText={`{{!! ${variable} !!}}`} />
                      </div>
                    </List.Item>
                  ))}
                </List>
              </StyledVariableList>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* TR-564 P0-2: Send test email */}
      <Modal
        open={isSendTestModal}
        onClose={handleCloseModal}
        title="Send test email"
        primaryAction={{
          content: cooldown > 0 ? `Send again in ${cooldown}s` : 'Send test email',
          onAction: handleSendTestEmail,
          loading: sendTestEmailData.isLoading,
          disabled: !canSendTest,
        }}
        secondaryActions={[{ content: 'Close', onAction: handleCloseModal }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <InlineStack gap="200" blockAlign="center">
              <Text as="span" tone="subdued">
                Testing:
              </Text>
              <Badge tone="info">{receiverLabel}</Badge>
              <Badge>{getStatusLabel(orderStatusState)}</Badge>
            </InlineStack>

            {sentTo && (
              <Banner tone="success">
                A test email has been sent to <strong>{sentTo}</strong>. Please check your inbox and spam folder to ensure that
                the email was delivered successfully.
              </Banner>
            )}

            {testError && (
              <Banner
                tone="critical"
                action={
                  senderEmail
                    ? { content: 'Verify sender', onAction: handleResendVerify, loading: resendSesEmailData.isLoading }
                    : undefined
                }
              >
                {testError}
              </Banner>
            )}

            <TextField
              label="Send test to"
              type="email"
              value={testEmail}
              onChange={(value) => {
                setTestEmail(value);
                if (!touched) setTouched(true);
              }}
              onBlur={() => setTouched(true)}
              autoComplete="email"
              requiredIndicator
              error={invalid ? 'Enter a valid email address.' : undefined}
              helpText="Only used for this test. Your saved notification recipient won't change."
              placeholder="name@example.com"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </StyledTemplateButtons>
  );
};

export default TemplateButtons;
